import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchStatusPulse } from '@/services/statusService';

const STATUS_DESCRIPTORS = {
  operational: {
    label: 'Operational',
    tone: 'success',
    fallback: 'All systems are behaving normally'
  },
  degraded: {
    label: 'Degraded',
    tone: 'warning',
    fallback: 'Some subsystems are experiencing latency'
  },
  outage: {
    label: 'Outage',
    tone: 'danger',
    fallback: 'We are working to restore service'
  },
  maintenance: {
    label: 'Maintenance',
    tone: 'accent',
    fallback: 'Scheduled maintenance is in progress'
  },
  unknown: {
    label: 'Unknown',
    tone: 'muted',
    fallback: 'Status data unavailable'
  }
};

const DEFAULT_PULSE = {
  indicator: 'unknown',
  label: STATUS_DESCRIPTORS.unknown.label,
  tone: STATUS_DESCRIPTORS.unknown.tone,
  message: STATUS_DESCRIPTORS.unknown.fallback,
  updatedAt: null,
  incidents: []
};

const FALLBACK_INTERVAL = 5 * 60 * 1000;
const configuredInterval = Number(import.meta.env.VITE_STATUS_POLL_MS);
const DEFAULT_INTERVAL = Number.isFinite(configuredInterval) && configuredInterval > 0
  ? configuredInterval
  : FALLBACK_INTERVAL;

export default function useStatusPulse(pollInterval = DEFAULT_INTERVAL) {
  const [pulse, setPulse] = useState(DEFAULT_PULSE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const intervalRef = useRef(null);

  const hydratePulse = useCallback((rawPulse) => {
    if (!rawPulse) return DEFAULT_PULSE;
    const descriptor = STATUS_DESCRIPTORS[rawPulse.indicator] || STATUS_DESCRIPTORS.unknown;
    return {
      indicator: rawPulse.indicator,
      label: descriptor.label,
      tone: descriptor.tone,
      message: rawPulse.incidents?.[0]?.name || rawPulse.description || descriptor.fallback || descriptor.label,
      updatedAt: rawPulse.updatedAt,
      incidents: rawPulse.incidents || [],
      description: rawPulse.description
    };
  }, []);

  const loadPulse = useCallback(async () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const result = await fetchStatusPulse({ signal: controller.signal });
      setPulse(hydratePulse(result));
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn('Status pulse fetch failed', err);
      setError(err.message || 'Unable to load platform status');
    } finally {
      setLoading(false);
    }
  }, [hydratePulse]);

  const refresh = useCallback(() => {
    loadPulse();
  }, [loadPulse]);

  const resolvedInterval = Number.isFinite(pollInterval) && pollInterval > 0 ? pollInterval : DEFAULT_INTERVAL;

  useEffect(() => {
    loadPulse();
    intervalRef.current = setInterval(() => {
      loadPulse();
    }, resolvedInterval);

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadPulse, resolvedInterval]);

  const meta = useMemo(() => ({
    isDegraded: pulse.indicator !== 'operational' && pulse.indicator !== 'unknown',
    hasIncidents: Array.isArray(pulse.incidents) && pulse.incidents.length > 0
  }), [pulse]);

  return {
    pulse,
    loading,
    error,
    refresh,
    meta
  };
}
